import {
  BookOpen,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Edit3,
  ExternalLink,
  FileText,
  Pause,
  Play,
  X,
  ArrowLeft,
  MessageSquare,
  Trash2,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import MegaLoader from "../../components/MegaLoader";
import { VerseDuration, HttpSuccessCodesObj } from "../../lib/data";
import { Endpoints } from "../../lib/endpoints";
import { NonPaginatedResponse } from "../../lib/responses";
import { PerformRequest, usePerformRequest } from "../../lib/usePerformRequest";
import { IHymnProps, ICommentProps } from "../../types";
import { motion, AnimatePresence } from "framer-motion";

const SingleHymn: React.FC = () => {
  const { hymnID } = useParams();
  const navigate = useNavigate();

  const { data: hymn, isLoading: isLoadingHymn } = usePerformRequest<
    NonPaginatedResponse<IHymnProps>
  >({
    method: Endpoints.GetHymn(hymnID as string).method,
    route: Endpoints.GetHymn(hymnID as string).route,
  });

  const {
    data: commentsData,
    isLoading: isLoadingComments,
    reloadData: reloadComments,
  } = usePerformRequest<NonPaginatedResponse<ICommentProps[]>>({
    method: Endpoints.GetComments(hymnID as string).method,
    route: Endpoints.GetComments(hymnID as string).route,
  });

  const [currentVerse, setCurrentVerse] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [showAuthorModal, setShowAuthorModal] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [progress, setProgress] = useState(0);
  const [deletingCommentId, setDeletingCommentId] = useState<string | null>(
    null
  );

  // Auto-advance verses when playing
  useEffect(() => {
    if (isPlaying) {
      const startTime = Date.now() - (progress / 100) * VerseDuration * 1000;
      const duration = VerseDuration * 1000;

      const interval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const newProgress = Math.min((elapsed / duration) * 100, 100);
        setProgress(newProgress);

        if (elapsed >= duration) {
          setCurrentVerse(
            (prev) => (prev + 1) % (hymn?.data?.verses?.length || 0)
          );
          setProgress(0);
        }
      }, 100);

      return () => clearInterval(interval);
    }
  }, [isPlaying, hymn?.data?.verses?.length, progress]);

  // Reset progress when verse changes manually
  useEffect(() => {
    setProgress(0);
  }, [currentVerse]);

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const nextVerse = () => {
    setCurrentVerse((prev) => (prev + 1) % (hymn?.data?.verses?.length || 0));
  };

  const prevVerse = () => {
    setCurrentVerse(
      (prev) =>
        (prev - 1 + (hymn?.data?.verses?.length || 0)) %
        (hymn?.data?.verses?.length || 0)
    );
  };

  const downloadPDF = async () => {
    if (!hymn?.data) return;

    setIsGeneratingPDF(true);

    try {
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>${hymn.data.title}</title>
          <style>
            body { font-family: 'Times New Roman', serif; line-height: 1.6; color: #333; max-width: 800px; margin: 0 auto; padding: 40px 20px; }
            .header { text-align: center; border-bottom: 2px solid #333; padding-bottom: 20px; margin-bottom: 30px; }
            .title { font-size: 32px; font-weight: bold; margin-bottom: 10px; }
            .metadata { font-size: 14px; color: #666; margin-bottom: 20px; }
            .summary { font-style: italic; text-align: center; margin-bottom: 30px; padding: 20px; background: #f9f9f9; border-radius: 8px; }
            .verse { margin-bottom: 30px; page-break-inside: avoid; }
            .verse-number { font-weight: bold; color: #666; margin-bottom: 10px; font-size: 14px; }
            .lyrics { font-size: 16px; line-height: 1.8; text-align: center; }
            .author-section { margin-top: 40px; padding-top: 20px; border-top: 1px solid #ccc; }
            .author-title { font-size: 20px; font-weight: bold; margin-bottom: 15px; text-align: center; }
            .author-info { text-align: center; font-size: 14px; color: #666; }
          </style>
        </head>
        <body>
          <div class="header">
            <div class="title">${hymn.data.title}</div>
            <div class="metadata">
              ${hymn.data.pub.year} • ${hymn.data.pub.copyright}
              ${hymn.data.author ? `• By ${hymn.data.author.name}` : ""}
            </div>
          </div>
          <div class="summary">"${hymn.data.summary}"</div>
          ${hymn.data.verses
            .map(
              (verse, index) => `
            <div class="verse">
              <div class="verse-number">Verse ${index + 1}</div>
              <div class="lyrics">${verse.lyrics}</div>
            </div>
          `
            )
            .join("")}
          ${
            hymn.data.author
              ? `
            <div class="author-section">
              <div class="author-title">About the Author</div>
              <div class="author-info">
                <strong>${hymn.data.author.name}</strong><br>
                ${hymn.data.author.profile}
              </div>
            </div>
          `
              : ""
          }
        </body>
        </html>
      `;

      const blob = new Blob([htmlContent], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${hymn.data.title
        .replace(/[^a-z0-9]/gi, "_")
        .toLowerCase()}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm("Are you sure you want to delete this comment?"))
      return;

    setDeletingCommentId(commentId);
    try {
      const res = await PerformRequest({
        method: Endpoints.DeleteComment(commentId).method,
        route: Endpoints.DeleteComment(commentId).route,
      });

      if (res && res.status === HttpSuccessCodesObj.Success) {
        reloadComments();
      }
    } catch (error) {
      console.error("Failed to delete comment:", error);
    } finally {
      setDeletingCommentId(null);
    }
  };

  const handleBackToHymns = () => {
    navigate("/d/hymns");
  };

  if (isLoadingHymn) return <MegaLoader />;
  if (!hymn?.data) return <div>Hymn not found</div>;

  return (
    <div className="min-h-screen bg-[#0a0a0a] relative overflow-hidden text-gray-100 font-sans pb-12">
      {/* Background Grain */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-10"
        >
          <motion.button
            whileHover={{ x: -5 }}
            onClick={handleBackToHymns}
            className="flex items-center space-x-2 text-gray-400 hover:text-white transition-colors duration-200 group w-fit"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">Back to Hymns</span>
          </motion.button>

          <div className="flex items-center space-x-3">
            <Link
              to={`/d/hymn/${hymnID}/edit`}
              className="px-4 py-2 text-sm text-gray-300 border border-gray-700 rounded-lg hover:bg-gray-800 hover:text-white transition-all duration-200 flex items-center space-x-2"
            >
              <Edit3 className="w-4 h-4" />
              <span>Edit</span>
            </Link>
            <button
              onClick={() => setShowAuthorModal(true)}
              className="px-4 py-2 text-sm text-gray-300 border border-gray-700 rounded-lg hover:bg-gray-800 hover:text-white transition-all duration-200"
            >
              About Author
            </button>
            <button
              onClick={downloadPDF}
              disabled={isGeneratingPDF}
              className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-500 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 shadow-lg shadow-blue-500/20"
            >
              {isGeneratingPDF ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4" />
                  <span>Download PDF</span>
                </>
              )}
            </button>
          </div>
        </motion.div>

        {/* Title Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="text-center mb-12"
        >
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-br from-white via-gray-200 to-gray-500 mb-6">
            {hymn.data.title}
          </h1>
          <div className="flex flex-wrap items-center justify-center gap-4 text-gray-400 text-sm md:text-base">
            <span className="flex items-center space-x-2 bg-gray-900/50 px-3 py-1 rounded-full border border-gray-800">
              <Calendar className="w-4 h-4" />
              <span>{hymn.data.pub.year}</span>
            </span>
            <span className="flex items-center space-x-2 bg-gray-900/50 px-3 py-1 rounded-full border border-gray-800">
              <FileText className="w-4 h-4" />
              <span>{hymn.data.pub.copyright}</span>
            </span>
            <span className="flex items-center space-x-2 bg-gray-900/50 px-3 py-1 rounded-full border border-gray-800">
              <BookOpen className="w-4 h-4" />
              <a
                href={hymn.data.author?.link}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-blue-400 transition-colors"
              >
                {hymn.data.author?.name}
              </a>
            </span>
          </div>
        </motion.div>

        {/* Summary */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="max-w-3xl mx-auto mb-16"
        >
          <div className="bg-gray-900/50 backdrop-blur-xl rounded-2xl p-8 border border-gray-800 shadow-xl relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-500 opacity-50" />
            <p className="text-lg text-gray-300 leading-relaxed text-center italic font-serif">
              "{hymn.data.summary}"
            </p>
          </div>
        </motion.div>

        {/* Current Verse Display */}
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto mb-16"
        >
          <div className="bg-gray-900 border border-gray-800 rounded-3xl p-8 md:p-12 shadow-2xl relative">
            <div className="absolute top-4 right-6 text-gray-600 font-mono text-sm">
              Verse {currentVerse + 1} / {hymn.data.verses.length}
            </div>

            <div
              className="text-2xl md:text-3xl lg:text-4xl text-white leading-relaxed font-medium text-center mb-10 min-h-[200px] flex items-center justify-center"
              style={{ whiteSpace: "pre-line" }}
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentVerse}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.4 }}
                >
                  {hymn.data.verses[currentVerse]?.lyrics}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Progress Bar */}
            <div className="mb-8">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-2 font-mono">
                <span>Auto-play progress</span>
                <span>
                  {Math.ceil(((100 - progress) / 100) * VerseDuration)}s
                </span>
              </div>
              <div className="w-full bg-gray-800 rounded-full h-1.5 overflow-hidden">
                <div
                  className="h-full bg-blue-500 rounded-full transition-all duration-100 ease-linear"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center justify-center space-x-6">
              <button
                onClick={prevVerse}
                className="p-3 rounded-full bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white transition-all duration-200"
              >
                <ChevronLeft className="w-6 h-6" />
              </button>

              <button
                onClick={togglePlay}
                className={`p-5 rounded-full shadow-lg transition-all duration-200 transform hover:scale-105 ${
                  isPlaying
                    ? "bg-blue-600 text-white shadow-blue-500/25"
                    : "bg-gray-800 text-gray-300 hover:bg-gray-700"
                }`}
              >
                {isPlaying ? (
                  <Pause className="w-8 h-8 fill-current" />
                ) : (
                  <Play className="w-8 h-8 fill-current translate-x-1" />
                )}
              </button>

              <button
                onClick={nextVerse}
                className="p-3 rounded-full bg-gray-800 text-gray-300 hover:bg-gray-700 hover:text-white transition-all duration-200"
              >
                <ChevronRight className="w-6 h-6" />
              </button>
            </div>
          </div>
        </motion.div>

        {/* All Verses Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-16">
          {hymn.data.verses.map((verse, index) => (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              key={verse.id}
              onClick={() => setCurrentVerse(index)}
              className={`p-6 rounded-2xl border cursor-pointer transition-all duration-300 group ${
                index === currentVerse
                  ? "bg-blue-900/10 border-blue-500/50 shadow-[0_0_20px_rgba(59,130,246,0.1)]"
                  : "bg-gray-900/30 border-gray-800 hover:border-gray-700 hover:bg-gray-900/50"
              }`}
            >
              <div className="flex items-center justify-between mb-4">
                <span
                  className={`text-xs font-bold uppercase tracking-wider px-2 py-1 rounded ${
                    index === currentVerse
                      ? "bg-blue-500/20 text-blue-400"
                      : "bg-gray-800 text-gray-500"
                  }`}
                >
                  Verse {index + 1}
                </span>
                {index === currentVerse && (
                  <CheckCircle2 className="w-5 h-5 text-blue-500" />
                )}
              </div>
              <p
                className={`text-sm leading-relaxed whitespace-pre-line ${
                  index === currentVerse
                    ? "text-gray-200"
                    : "text-gray-400 group-hover:text-gray-300"
                }`}
              >
                {verse.lyrics}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Author Section */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="max-w-2xl mx-auto text-center mb-16"
        >
          <div className="w-20 h-20 bg-gradient-to-br from-gray-800 to-gray-900 rounded-full flex items-center justify-center text-2xl font-bold text-gray-300 mx-auto mb-6 border border-gray-700 shadow-xl">
            {hymn.data.author?.name
              .split(" ")
              .map((n) => n[0])
              .join("")}
          </div>
          <h3 className="text-xl font-bold text-white mb-3">
            About {hymn.data.author?.name}
          </h3>
          <p className="text-gray-400 leading-relaxed mb-6">
            {hymn.data.author?.profile}
          </p>
          {hymn.data.author?.link && (
            <a
              href={hymn.data.author?.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors"
            >
              <span>Full Biography</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </motion.div>

        {/* Comments Section */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="flex items-center space-x-3 mb-8">
            <MessageSquare className="w-6 h-6 text-blue-400" />
            <h2 className="text-2xl font-bold text-white">Comments</h2>
            <span className="bg-gray-800 text-gray-400 px-3 py-1 rounded-full text-sm font-medium">
              {commentsData?.data?.length || 0}
            </span>
          </div>

          {isLoadingComments ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : commentsData?.data && commentsData.data.length > 0 ? (
            <div className="space-y-4">
              {commentsData.data.map((comment) => (
                <div
                  key={comment.id}
                  className="bg-gray-900/50 border border-gray-800 rounded-xl p-6 transition-all hover:bg-gray-900"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-4">
                      <div className="w-10 h-10 bg-gradient-to-br from-purple-500/20 to-blue-500/20 rounded-full flex items-center justify-center text-gray-300 font-bold border border-gray-700">
                        {comment.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <h4 className="font-semibold text-white">
                          {comment.name}
                        </h4>
                        <p className="text-xs text-gray-500 mb-2">
                          {new Date(comment.createdAt).toLocaleDateString()} at{" "}
                          {new Date(comment.createdAt).toLocaleTimeString()}
                        </p>
                        <p className="text-gray-300 leading-relaxed text-sm">
                          {comment.content}
                        </p>
                      </div>
                    </div>

                    <button
                      onClick={() => handleDeleteComment(comment.id)}
                      disabled={deletingCommentId === comment.id}
                      className="p-2 text-gray-500 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all duration-200"
                      title="Delete Comment"
                    >
                      {deletingCommentId === comment.id ? (
                        <div className="w-5 h-5 border-2 border-red-500/30 border-t-red-500 rounded-full animate-spin" />
                      ) : (
                        <Trash2 className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-900/30 border border-gray-800 rounded-2xl border-dashed">
              <MessageSquare className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500">No comments yet.</p>
            </div>
          )}
        </div>

        {/* Author Modal */}
        <AnimatePresence>
          {showAuthorModal && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
              onClick={() => setShowAuthorModal(false)}
            >
              <motion.div
                initial={{ scale: 0.9, y: 20 }}
                animate={{ scale: 1, y: 0 }}
                exit={{ scale: 0.9, y: 20 }}
                className="bg-gray-900 border border-gray-700 rounded-2xl p-8 max-w-lg w-full relative shadow-2xl"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setShowAuthorModal(false)}
                  className="absolute top-4 right-4 p-2 text-gray-500 hover:text-white transition-colors rounded-lg hover:bg-gray-800"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="text-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-full flex items-center justify-center text-xl font-bold text-white mx-auto mb-4 border border-white/10">
                    {hymn.data.author?.name.charAt(0)}
                  </div>
                  <h3 className="text-xl font-bold text-white">
                    {hymn.data.author?.name}
                  </h3>
                </div>

                <div className="text-gray-300 leading-relaxed mb-8 text-center text-sm">
                  {hymn.data.author?.profile}
                </div>

                <div className="flex justify-center">
                  <a
                    href={hymn.data.author?.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-6 py-2.5 bg-white text-black rounded-xl font-medium hover:bg-gray-200 transition-colors"
                  >
                    Visit Website
                  </a>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SingleHymn;
